/**
 * useSnooze Hook
 *
 * Snooze message, list snoozed, cancel snooze, return due via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailSnoozedMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useSnooze(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.snoozed(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account');
      return api.listSnoozedEmails(accountId);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    if (!accountId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.email.snoozed(accountId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.email.all });
  }, [accountId, queryClient]);

  const snooze = useCallback(
    async (messageId: string, snoozeUntil: string) => {
      if (!accountId) return;
      const result = await api.snoozeEmail({ accountId, messageId, snoozeUntil });
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const updateSnooze = useCallback(
    async (snoozeId: string, snoozeUntil: string) => {
      if (!accountId) return;
      const result = await api.updateEmailSnooze(snoozeId, accountId, snoozeUntil);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const cancelSnooze = useCallback(
    async (snoozeId: string) => {
      if (!accountId) return;
      const result = await api.cancelEmailSnooze(snoozeId, accountId);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const returnDue = useCallback(async () => {
    if (!accountId) return;
    const result = await api.returnDueSnoozedEmails(accountId);
    invalidate();
    return result;
  }, [accountId, invalidate]);

  return {
    snoozed: queryResult.data?.snoozed ?? ([] as EmailSnoozedMessage[]),
    snoozedCount: queryResult.data?.snoozed?.length ?? 0,
    loading: queryResult.isLoading,
    snooze,
    updateSnooze,
    cancelSnooze,
    returnDue,
    refresh: queryResult.refetch,
  };
}
